import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ConsultationForm from '../../ConsultationForm';
import './index.css';

interface Product {
  id: string;
  name: string;
  images: string[];
  summary: string;
  details: string;
}

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    const loadProduct = async () => {
      if (!productId) {
        setError(t('productDetail.productIdMissing'));
        setLoading(false);
        return;
      }

      // 重置状态，准备重新加载
      setLoading(true);
      setError(null);

      try {
        // 获取当前语言的辅助函数
        const getCurrentLanguage = (): string => {
          // 方法1: 优先从 localStorage 读取（最可靠，所有标签页共享）
          const storedLang = localStorage.getItem('i18nextLng');
          if (storedLang) {
            const lang = storedLang.split('-')[0];
            console.log('[ProductDetail] Language from localStorage:', lang);
            return lang;
          }
          
          // 方法2: 从 i18n.language 读取
          if (i18n.language) {
            const lang = i18n.language.split('-')[0];
            console.log('[ProductDetail] Language from i18n.language:', lang);
            return lang;
          }
          
          // 方法3: 默认中文
          console.log('[ProductDetail] Using default language: zh');
          return 'zh';
        };

        // 等待一小段时间，确保 localStorage 已经被正确设置
        // 这对于新标签页打开时特别重要
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const currentLang = getCurrentLanguage();
        
        // 根据当前语言加载对应的产品数据
        let response: Response | null = null;
        let productData: Product | null = null;
        
        if (currentLang === 'en') {
          // 尝试加载英文版本
          try {
            response = await fetch(`/data/products/en/${productId}.json`);
            if (response.ok) {
              productData = await response.json();
            }
          } catch (err) {
            console.warn(`英文版本不存在或加载失败，回退到中文版本: ${productId}`, err);
          }
        }
        
        // 如果英文版本不存在或加载失败，回退到中文版本
        if (!productData) {
          try {
            response = await fetch(`/data/products/${productId}.json`);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            productData = await response.json();
          } catch (err) {
            console.error('加载中文版本也失败:', err);
            throw new Error('产品数据加载失败');
          }
        }
        
        if (productData) {
          setProduct(productData);
        } else {
          throw new Error('产品数据为空');
        }
      } catch (err) {
        console.error('加载商品数据失败:', err);
        setError(t('productDetail.productNotFound'));
      } finally {
        setLoading(false);
      }
    };

    // 立即尝试加载
    loadProduct();

    // 如果 i18n 还未初始化，等待初始化完成后再次检查语言设置
    // 这对于新标签页打开时特别重要
    if (!i18n.isInitialized) {
      const initHandler = () => {
        console.log('[ProductDetail] i18n initialized, reloading product');
        // i18n 初始化完成后，重新加载以确保语言设置正确
        loadProduct();
        i18n.off('initialized', initHandler);
      };
      i18n.on('initialized', initHandler);
    } else {
      // 即使 i18n 已初始化，也等待一小段时间确保 localStorage 同步
      // 这对于新标签页打开时特别重要
      setTimeout(() => {
        const storedLang = localStorage.getItem('i18nextLng');
        const i18nLang = i18n.language?.split('-')[0] || 'zh';
        const storedLangBase = storedLang ? storedLang.split('-')[0] : null;
        
        // 如果 localStorage 和 i18n.language 不一致，重新加载
        if (storedLangBase && storedLangBase !== i18nLang) {
          console.log('[ProductDetail] Language mismatch detected, reloading. localStorage:', storedLangBase, 'i18n:', i18nLang);
          loadProduct();
        }
      }, 200);
    }

    // 监听语言变化事件，确保语言切换时重新加载
    const handleLanguageChanged = () => {
      loadProduct();
    };

    i18n.on('languageChanged', handleLanguageChanged);

    // 清理函数
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [productId, i18n, t]);

  // 错误时倒计时跳转到首页
  useEffect(() => {
    if (error && !loading) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [error, loading, navigate]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
  };

  if (loading) {
    return (
      <main className="product-detail-container">
        <div className="product-detail-loading">
          <div className="loading-spinner"></div>
          <p>{t('productDetail.loading')}</p>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="product-detail-container">
        <div className="product-detail-error">
          <h2>{t('productDetail.loadError')}</h2>
          <p>{error || t('productDetail.unknownError')}</p>
          <div className="countdown-container">
            <p className="countdown-text">
              {t('productDetail.redirectCountdown')} {countdown} {i18n.language === 'zh' ? '秒' : 'seconds'}
            </p>
            <div className="countdown-progress">
              <div 
                className="countdown-bar" 
                style={{ width: `${(countdown / 10) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="product-detail-container">
      <div className="product-detail-card">
        {/* 商品主要信息区域 */}
        <section className="product-main-section">
          <div className="container">
            <div className="product-main-grid">
              {/* 商品图片区域 */}
              <div className="product-images">
                <div className="main-image-container">
                  <img 
                    src={product.images[selectedImageIndex]} 
                    alt={product.name}
                    className="main-image"
                    onError={handleImageError}
                  />
                </div>
                
                {product.images.length > 1 && (
                  <div className="image-thumbnails">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                        onClick={() => setSelectedImageIndex(index)}
                      >
                        <img 
                          src={image} 
                          alt={`${product.name} - ${t('productDetail.imageAlt')} ${index + 1}`}
                          onError={handleImageError}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 商品信息区域 */}
              <div className="product-info">
                <h1 className="product-title">{product.name}</h1>
                <p className="product-summary">{product.summary}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 商品详情区域 */}
        <section className="product-details-section">
          <div className="container">
            <div className="details-content">
              <div className="markdown-content">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: (props: any) => <h1 className="md-h1">{props.children}</h1>,
                    h2: (props: any) => <h2 className="md-h2">{props.children}</h2>,
                    h3: (props: any) => <h3 className="md-h3">{props.children}</h3>,
                    h4: (props: any) => <h4 className="md-h4">{props.children}</h4>,
                    p: (props: any) => <p className="md-p">{props.children}</p>,
                    ul: (props: any) => <ul className="md-ul">{props.children}</ul>,
                    ol: (props: any) => <ol className="md-ol">{props.children}</ol>,
                    li: (props: any) => <li className="md-li">{props.children}</li>,
                    blockquote: (props: any) => <blockquote className="md-blockquote">{props.children}</blockquote>,
                    code: (props: any) => {
                      const isInline = !props.className;
                      return isInline ? 
                        <code className="md-code-inline">{props.children}</code> :
                        <code className="md-code-block">{props.children}</code>;
                    },
                    pre: (props: any) => <pre className="md-pre">{props.children}</pre>,
                    table: (props: any) => <table className="md-table">{props.children}</table>,
                    thead: (props: any) => <thead className="md-thead">{props.children}</thead>,
                    tbody: (props: any) => <tbody className="md-tbody">{props.children}</tbody>,
                    tr: (props: any) => <tr className="md-tr">{props.children}</tr>,
                    th: (props: any) => <th className="md-th">{props.children}</th>,
                    td: (props: any) => <td className="md-td">{props.children}</td>,
                    hr: () => <hr className="md-hr" />,
                    strong: (props: any) => <strong className="md-strong">{props.children}</strong>,
                    em: (props: any) => <em className="md-em">{props.children}</em>,
                  }}
                >
                  {product.details}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </section>

        {/* 产品咨询表单 */}
        <section className="consultation-section">
          <div className="container">
            <ConsultationForm productName={product.name} />
          </div>
        </section>
      </div>
    </main>
  );
};

export default ProductDetail;
